/**
 * AssertionsEditor — reusable UI for defining Workflow Scenario
 * assertions.
 *
 * Assertions:
 *   status_equals            expected: "running" | "completed" | "paused" | "failed" | "cancelled"
 *   current_step_key_equals  expected: step key (free text; step keys are stable slugs)
 *   context_equals           key: dotted path · expected: any (auto-parsed as JSON when possible)
 *
 * Value coercion:
 *   For context_equals we try JSON.parse first (so "true", "42", "\"foo\"",
 *   arrays and objects all work) — falling back to the raw string.
 */
import { Plus, Trash2 } from "lucide-react";

const ASSERTION_TYPES = [
  { value: "status_equals",           label: "Run status equals" },
  { value: "current_step_key_equals", label: "Current step key equals" },
  { value: "context_equals",          label: "Context value equals" },
];

const STATUS_OPTIONS = ["running", "paused", "completed", "cancelled", "failed"];

export function coerceExpected(raw) {
  if (raw === "" || raw == null) return "";
  try {
    return JSON.parse(raw);
  } catch {
    return raw;
  }
}

export function formatExpected(val) {
  if (val === null || val === undefined) return "";
  if (typeof val === "string") return val;
  try {
    return JSON.stringify(val);
  } catch {
    return String(val);
  }
}

export default function AssertionsEditor({ assertions, onChange, testIdPrefix = "assertion" }) {
  const list = Array.isArray(assertions) ? assertions : [];

  const update = (idx, patch) => {
    const next = list.map((a, i) => (i === idx ? { ...a, ...patch } : a));
    onChange(next);
  };

  const add = () => {
    onChange([...list, { type: "status_equals", key: "", expected: "completed" }]);
  };

  const remove = (idx) => {
    onChange(list.filter((_, i) => i !== idx));
  };

  return (
    <div className="space-y-2" data-testid={`${testIdPrefix}-editor`}>
      {list.length === 0 && (
        <p className="text-[11px] text-slate-500 italic">
          No assertions yet. Add one to convert this scenario into a pass/fail regression check.
        </p>
      )}

      {list.map((a, idx) => {
        const showKey = a.type === "context_equals";
        const expectedInput = formatExpected(a.expected);
        return (
          <div
            key={idx}
            data-testid={`${testIdPrefix}-row-${idx}`}
            className="rounded-lg border border-slate-200 bg-white p-2 flex flex-col gap-1.5"
          >
            <div className="flex items-center gap-2">
              <select
                value={a.type}
                onChange={(e) => update(idx, { type: e.target.value })}
                data-testid={`${testIdPrefix}-type-${idx}`}
                className="border border-slate-300 rounded-md px-2 py-1 text-xs flex-1"
              >
                {ASSERTION_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => remove(idx)}
                className="text-slate-400 hover:text-rose-500"
                data-testid={`${testIdPrefix}-remove-${idx}`}
                title="Remove assertion"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>

            {showKey && (
              <input
                type="text"
                value={a.key || ""}
                onChange={(e) => update(idx, { key: e.target.value })}
                placeholder="Context key (e.g. score or patient.tier)"
                data-testid={`${testIdPrefix}-key-${idx}`}
                className="border border-slate-300 rounded-md px-2 py-1 text-xs"
              />
            )}

            {a.type === "status_equals" ? (
              <select
                value={typeof a.expected === "string" ? a.expected : ""}
                onChange={(e) => update(idx, { expected: e.target.value })}
                data-testid={`${testIdPrefix}-expected-${idx}`}
                className="border border-slate-300 rounded-md px-2 py-1 text-xs"
              >
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                value={expectedInput}
                onChange={(e) => update(idx, { expected: coerceExpected(e.target.value) })}
                placeholder={
                  a.type === "context_equals"
                    ? "Expected value (JSON allowed — true, 42, \"tag\", {...})"
                    : "Expected step key (e.g. lab_reviewed)"
                }
                data-testid={`${testIdPrefix}-expected-${idx}`}
                className="border border-slate-300 rounded-md px-2 py-1 text-xs font-mono"
              />
            )}
          </div>
        );
      })}

      <button
        type="button"
        onClick={add}
        data-testid={`${testIdPrefix}-add`}
        className="inline-flex items-center gap-1 text-[11px] font-semibold text-violet-700 hover:text-violet-900"
      >
        <Plus className="w-3 h-3" /> Add assertion
      </button>
    </div>
  );
}
